import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import AdminLayout from "./layout/AdminLayout";

/**
 * Lazy-loaded page modules.
 * Each module is independently bundled — adding a new module
 * never bloats the initial admin-portal chunk.
 */
const DashboardPage   = lazy(() => import("./modules/dashboard/DashboardPage"));
const UsersPage       = lazy(() => import("./modules/users/UsersPage"));
const JobsPage        = lazy(() => import("./modules/jobs/JobsPage"));
const ComingSoonPage  = lazy(() => import("./modules/coming-soon/ComingSoonPage"));

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <span className="text-sm text-slate-400">Loading…</span>
    </div>
  );
}

/**
 * Root of the Admin Portal.
 * Mounted at /admin-portal/* in App.js — completely outside the
 * main <Layout> so it has its own chrome (Header + Sidebar).
 *
 * To add a new module:
 *   1. Create src/admin-portal/modules/<name>/<Name>Page.jsx
 *   2. lazy-import it above
 *   3. Add a <Route> below
 *   4. Add its entry to lib/nav.js (remove comingSoon: true)
 */
export default function AdminPortal() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route element={<AdminLayout />}>

          {/* Default → Dashboard */}
          <Route index element={<Navigate to="dashboard" replace />} />

          {/* ── Live modules ── */}
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="users"     element={<UsersPage />} />
          <Route path="ai-jobs"   element={<JobsPage />} />

          {/* ── Users & Roles stubs ── */}
          <Route path="users/admins"      element={<ComingSoonPage title="Admins" />} />
          <Route path="users/permissions" element={<ComingSoonPage title="Permissions" />} />

          {/* ── Content Management ── */}
          <Route path="content/articles"   element={<ComingSoonPage title="Articles" />} />
          <Route path="content/categories" element={<ComingSoonPage title="Categories" />} />
          <Route path="content/tags"       element={<ComingSoonPage title="Tags" />} />

          {/* ── SEO Studio stubs ── */}
          <Route path="seo/pages"   element={<ComingSoonPage title="Generated Pages" />} />
          <Route path="seo/csv"     element={<ComingSoonPage title="CSV Uploads" />} />
          <Route path="seo/sitemap" element={<ComingSoonPage title="Sitemap Management" />} />

          {/* ── Geo Insights stubs ── */}
          <Route path="geo/overview"  element={<ComingSoonPage title="Visitor Analytics" />} />
          <Route path="geo/countries" element={<ComingSoonPage title="Countries" />} />

          {/* ── Analytics stubs ── */}
          <Route path="analytics/traffic" element={<ComingSoonPage title="Traffic Overview" />} />
          <Route path="analytics/pages"   element={<ComingSoonPage title="Top Pages" />} />

          {/* ── System stubs ── */}
          <Route path="system/settings" element={<ComingSoonPage title="Settings" />} />
          <Route path="system/audit"    element={<ComingSoonPage title="Audit Logs" />} />
          <Route path="system/config"   element={<ComingSoonPage title="Configuration" />} />

          {/* Unknown path → back to dashboard */}
          <Route path="*" element={<Navigate to="dashboard" replace />} />

        </Route>
      </Routes>
    </Suspense>
  );
}
